'use client';
import React, { useState, useEffect } from 'react';
import { Download, Calendar } from 'lucide-react';
import dynamic from 'next/dynamic';
import { createClient } from '@/lib/supabase/client';

const LeadVolumeBySourceChart = dynamic(() => import('./LeadVolumeBySourceChart'), { ssr: false });
const ConversionFunnelChart = dynamic(() => import('./ConversionFunnelChart'), { ssr: false });
const LeadsByCheckpointChart = dynamic(() => import('../../components/LeadsByCheckpointChart'), { ssr: false });

interface SourceRow {
  source: string;
  leads: number;
  contacted: number;
  interested: number;
  converted: number;
  rate: string;
}

const dateRanges = ['Last 7 days', 'Last 30 days', 'Last 90 days', 'All Time'];

function getDaysSince(range: string): number | null {
  if (range === 'Last 7 days') return 7;
  if (range === 'Last 30 days') return 30;
  if (range === 'Last 90 days') return 90;
  return null; // All Time
}

export default function ReportsClient() {
  const [dateRange, setDateRange] = useState('Last 30 days');
  const [sourceTable, setSourceTable] = useState<SourceRow[]>([]);
  const [tableLoading, setTableLoading] = useState(true);

  useEffect(() => {
    async function fetchSourceTable() {
      setTableLoading(true);
      const supabase = createClient();
      const days = getDaysSince(dateRange);

      let query = supabase.from('leads').select('source, stage');
      if (days) {
        const since = new Date();
        since.setDate(since.getDate() - days);
        query = query.gte('created_at', since.toISOString());
      }

      const { data: leads, error } = await query;

      if (error || !leads) {
        setTableLoading(false);
        return;
      }

      const sourceMap: Record<string, { leads: number; contacted: number; interested: number; converted: number }> = {};

      leads.forEach((lead) => {
        const src = lead.source || 'Unknown';
        if (!sourceMap[src]) sourceMap[src] = { leads: 0, contacted: 0, interested: 0, converted: 0 };
        sourceMap[src].leads += 1;
        if (['Contacted', 'Interested', 'Counseling Scheduled', 'Applied', 'Converted'].includes(lead.stage)) {
          sourceMap[src].contacted += 1;
        }
        if (['Interested', 'Counseling Scheduled', 'Applied', 'Converted'].includes(lead.stage)) {
          sourceMap[src].interested += 1;
        }
        if (lead.stage === 'Converted') {
          sourceMap[src].converted += 1;
        }
      });

      const rows: SourceRow[] = Object.entries(sourceMap)
        .map(([source, counts]) => ({
          source,
          ...counts,
          rate: counts.leads > 0 ? `${((counts.converted / counts.leads) * 100).toFixed(1)}%` : '0%',
        }))
        .sort((a, b) => b.leads - a.leads);

      setSourceTable(rows);
      setTableLoading(false);
    }

    fetchSourceTable();
  }, [dateRange]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Reports & Analytics</h1>
          <p className="text-sm text-muted-foreground mt-0.5">NEET 2026 Season · Source attribution & funnel analysis</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 border border-border rounded-lg px-3 py-2 bg-card">
            <Calendar size={14} className="text-muted-foreground" />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e?.target?.value)}
              className="text-sm font-medium text-foreground bg-transparent focus:outline-none"
            >
              {dateRanges?.map((r) => <option key={`range-${r}`} value={r}>{r}</option>)}
            </select>
          </div>
          <button className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted transition-colors">
            <Download size={15} />
            Export
          </button>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-base font-semibold text-foreground mb-1">Lead Volume by Source</h3>
          <p className="text-xs text-muted-foreground mb-4">Weekly lead intake — MBBS Lighthouse, GMC, Neet Navigator</p>
          <LeadVolumeBySourceChart />
        </div>
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-base font-semibold text-foreground mb-1">Pipeline Conversion Funnel</h3>
          <p className="text-xs text-muted-foreground mb-4">Stage-by-stage drop-off — All leads</p>
          <ConversionFunnelChart />
        </div>
      </div>

      {/* Charts Row 2 — Leads by Checkpoint full width */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="text-base font-semibold text-foreground mb-1">Leads by Checkpoint</h3>
        <p className="text-xs text-muted-foreground mb-4">Lead count per checkpoint stage</p>
        <LeadsByCheckpointChart />
      </div>

      {/* Source Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-base font-semibold text-foreground">Source-wise Conversion Rates</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{dateRange}</p>
        </div>
        <div className="overflow-x-auto scrollbar-thin">
          {tableLoading ? (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">Loading...</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 border-b border-border">
                  {['Source', 'Total Leads', 'Contacted', 'Interested', 'Converted', 'Conv. Rate']?.map((h) => (
                    <th key={`src-th-${h}`} className="px-5 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sourceTable.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-8 text-center text-sm text-muted-foreground">No data for selected period</td>
                  </tr>
                ) : (
                  sourceTable?.map((row, idx) => (
                    <tr key={`src-row-${idx}`} className="border-b border-border hover:bg-muted/30 transition-colors">
                      <td className="px-5 py-3 font-semibold text-foreground">{row?.source}</td>
                      <td className="px-5 py-3 tabular-nums text-foreground">{row?.leads?.toLocaleString('en-IN')}</td>
                      <td className="px-5 py-3 tabular-nums text-muted-foreground">{row?.contacted?.toLocaleString('en-IN')}</td>
                      <td className="px-5 py-3 tabular-nums text-muted-foreground">{row?.interested?.toLocaleString('en-IN')}</td>
                      <td className="px-5 py-3 tabular-nums font-semibold text-success">{row?.converted?.toLocaleString('en-IN')}</td>
                      <td className="px-5 py-3">
                        <span className="font-bold text-primary tabular-nums">{row?.rate}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}